import React, {
  Suspense,
  Fragment,
  lazy
} from 'react';
import {
  Switch,
  Redirect,
  Route
} from 'react-router-dom';
import DashboardLayout from 'src/layouts/DashboardLayout';
import MainLayout from 'src/layouts/MainLayout';
import LoadingScreen from 'src/components/LoadingScreen';
import RutaProtegida from 'src/components/RutaProtegida';
import Permisos from 'src/constantes/permisos';

// Helper: envuelve un componente lazy con RutaProtegida
const protegida = (permiso, importer) => {
  const Componente = lazy(importer);
  return (props) => (
    <RutaProtegida permiso={permiso}>
      <Componente {...props} />
    </RutaProtegida>
  );
};

export const renderRoutes = (routes = []) => (
  <Suspense fallback={<LoadingScreen />}>
    <Switch>
      {routes.map((route, i) => {
        const Layout = route.layout || Fragment;
        const Component = route.component;

        return (
          <Route
            key={i}
            path={route.path}
            exact={route.exact}
            render={(props) => (
              <Layout>
                {route.routes
                  ? renderRoutes(route.routes)
                  : <Component {...props} />}
              </Layout>
            )}
          />
        );
      })}
    </Switch>
  </Suspense>
);

const routes = [
  {
    exact: true,
    path: '/404',
    component: lazy(() => import('src/views/errors/NotFoundView')),
  },
  {
    exact: true,
    path: '/sin-permisos',
    component: lazy(() => import('src/views/errors/SinPermisosView')),
  },
  {
    path: '/app',
    layout: DashboardLayout,
    routes: [
      {
        exact: true,
        path: '/app',
        component: () => <Redirect to="/app/reports/dashboard" />
      },
      {
        exact: true,
        path: '/app/configuracion/eps',
        component: protegida(Permisos.EPS_VER, () => import('src/views/configuracion/eps')),
      },
      {
        exact: true,
        path: '/app/configuracion/especialidades',
        component: protegida(Permisos.ESPECIALIDAD_VER, () => import('src/views/configuracion/especialidades')),
      },
      {
        exact: true,
        path: '/app/configuracion/especialidades/new',
        component: protegida(Permisos.ESPECIALIDAD_CREAR, () => import('src/views/configuracion/especialidades/New')),
      },
      {
        exact: true,
        path: '/app/configuracion/especialidades/edit/:value',
        component: protegida(Permisos.ESPECIALIDAD_EDITAR, () => import('src/views/configuracion/especialidades/Edit')),
      },
      {
        exact: true,
        path: '/app/configuracion/tipoafiliacion',
        component: protegida(Permisos.TIPOAFILIACION_VER, () => import('src/views/configuracion/tipoAfiliacion')),
      },
      {
        exact: true,
        path: '/app/configuracion/diagnostico/new',
        component: protegida(Permisos.DIAGNOSTICO_CREAR, () => import('src/views/configuracion/diagnostico/New')),
      },
      {
        exact: true,
        path: '/app/configuracion/diagnostico/edit/:value',
        component: protegida(Permisos.DIAGNOSTICO_EDITAR, () => import('src/views/configuracion/diagnostico/Edit')),
      },
      {
        exact: true,
        path: '/app/configuracion/diagnostico',
        component: protegida(Permisos.DIAGNOSTICO_VER, () => import('src/views/configuracion/diagnostico/List')),
      },
      {
        exact: true,
        path: '/app/configuracion/institucion',
        component: protegida(Permisos.INSTITUCION_VER, () => import('src/views/configuracion/instituciones')),
      },
      {
        exact: true,
        path: '/app/configuracion/pacientes/new',
        component: protegida(Permisos.PACIENTE_CREAR, () => import('src/views/configuracion/pacientes/New')),
      },
      {
        exact: true,
        path: '/app/configuracion/pacientes/edit/:value',
        component: protegida(Permisos.PACIENTE_EDITAR, () => import('src/views/configuracion/pacientes/Edit')),
      },
      {
        exact: true,
        path: '/app/configuracion/pacientes',
        component: protegida(Permisos.PACIENTE_VER, () => import('src/views/configuracion/pacientes/List')),
      },
      {
        exact: true,
        path: '/app/configuracion/cirujanos/new',
        component: protegida(Permisos.CIRUJANO_CREAR, () => import('src/views/configuracion/cirujano/New')),
      },
      {
        exact: true,
        path: '/app/configuracion/cirujanos/edit/:value',
        component: protegida(Permisos.CIRUJANO_EDITAR, () => import('src/views/configuracion/cirujano/Edit')),
      },
      {
        exact: true,
        path: '/app/configuracion/cirujanos',
        component: protegida(Permisos.CIRUJANO_VER, () => import('src/views/configuracion/cirujano/List')),
      },
      {
        exact: true,
        path: '/app/configuracion/fellow',
        component: protegida(Permisos.FELLOW_VER, () => import('src/views/configuracion/fellow')),
      },
      {
        exact: true,
        path: '/app/configuracion/subespecialidad',
        component: protegida(Permisos.SUBESPECIALIDAD_VER, () => import('src/views/configuracion/subEspecialidad')),
      },
      {
        exact: true,
        path: '/app/configuracion/subespecialidad/new',
        component: protegida(Permisos.SUBESPECIALIDAD_CREAR, () => import('src/views/configuracion/subEspecialidad/New')),
      },
      {
        exact: true,
        path: '/app/configuracion/subespecialidad/edit/:value',
        component: protegida(Permisos.SUBESPECIALIDAD_EDITAR, () => import('src/views/configuracion/subEspecialidad/Edit')),
      },
      {
        exact: true,
        path: '/app/configuracion/universidad',
        component: protegida(Permisos.UNIVERSIDAD_VER, () => import('src/views/configuracion/universidad')),
      },
      {
        exact: true,
        path: '/app/operacion/prepedido/new',
        component: protegida(Permisos.PREPEDIDO_CREAR, () => import('src/views/procesos/prepedido/New')),
      },
      {
        exact: true,
        path: '/app/operacion/prepedido/edit/:value',
        component: protegida(Permisos.PREPEDIDO_EDITAR, () => import('src/views/procesos/prepedido/Edit')),
      },
      {
        exact: true,
        path: '/app/operacion/prepedido',
        component: protegida(Permisos.PREPEDIDO_VER, () => import('src/views/procesos/prepedido/List')),
      },
      {
        exact: true,
        path: '/app/operacion/asignacionsoportequirurgico/new',
        component: protegida(Permisos.ASIGNACIONSOPORTEQUIRURGICO_CREAR, () => import('src/views/procesos/asignacionSoporteQuirurgico/New')),
      },
      {
        exact: true,
        path: '/app/operacion/asignacionsoportequirurgico/new/:uuid',
        component: protegida(Permisos.ASIGNACIONSOPORTEQUIRURGICO_CREAR, () => import('src/views/procesos/asignacionSoporteQuirurgico/NewForm')),
      },
      {
        exact: true,
        path: '/app/operacion/asignacionsoportequirurgico/edit/:value',
        component: protegida(Permisos.ASIGNACIONSOPORTEQUIRURGICO_EDITAR, () => import('src/views/procesos/asignacionSoporteQuirurgico/Edit')),
      },
      {
        exact: true,
        path: '/app/operacion/asignacionsoportequirurgico',
        component: protegida(Permisos.ASIGNACIONSOPORTEQUIRURGICO_VER, () => import('src/views/procesos/asignacionSoporteQuirurgico/List')),
      },
      {
        exact: true,
        path: '/app/operacion/reportegastos/new',
        component: protegida(Permisos.REPORTEGASTO_CREAR, () => import('src/views/procesos/reporteGastos/NewForm')),
      },
      {
        exact: true,
        path: '/app/operacion/reportegastos/new/:uuid',
        component: protegida(Permisos.REPORTEGASTO_CREAR, () => import('src/views/procesos/reporteGastos/New')),
      },
      {
        exact: true,
        path: '/app/operacion/reportegastos/edit/:uuid/:value',
        component: protegida(Permisos.REPORTEGASTO_EDITAR, () => import('src/views/procesos/reporteGastos/Edit')),
      },
      {
        exact: true,
        path: '/app/operacion/reportegastos',
        component: protegida(Permisos.REPORTEGASTO_VER, () => import('src/views/procesos/reporteGastos/List')),
      },
      {
        exact: true,
        path: '/app/configuracion/seguridad/usuarios',
        component: protegida(Permisos.ROL_VER, () => import('src/views/configuracion/seguridad/usuarios')),
      },
      {
        exact: true,
        path: '/app/configuracion/seguridad/roles',
        component: protegida(Permisos.ROL_VER, () => import('src/views/configuracion/seguridad/roles')),
      },
      {
        exact: true,
        path: '/app/configuracion/seguridad/roles/new',
        component: protegida(Permisos.ROL_CREAR, () => import('src/views/configuracion/seguridad/roles/New')),
      },
      {
        exact: true,
        path: '/app/configuracion/seguridad/roles/edit/:value',
        component: protegida(Permisos.ROL_EDITAR, () => import('src/views/configuracion/seguridad/roles/Edit')),
      },
      {
        component: () => <Redirect to="/404" />
      }
    ]
  },
  {
    path: '*',
    layout: MainLayout,
    routes: [
      {
        path: '/',
        component: () => <Redirect to="/app/operacion/prepedido" />
      },
      {
        component: () => <Redirect to="/404" />
      }
    ]
  }
];

export default routes;
